import 'react-datepicker/dist/react-datepicker.css';

import BaseDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import dayjs from 'dayjs';
import type { MarkOptional } from 'ts-essentials';
import { Input } from '@vechaiui/react';
import { useField } from 'formik';

import Field from '#/components/forms/Field';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';

const DatePicker: React.FC<DatePickerProps> = ({
  children: _children,
  invalid,
  placeholder,
  ...props
}) => {
  return (
    <BaseDatePicker
      {...props}
      ariaInvalid={`${invalid}`}
      placeholderText={placeholder}
      selected={props.value ? new Date(props.value) : null}
    />
  );
};
DatePicker.defaultProps = {
  customInput: <Input size="lg" />,
};
export type DatePickerProps = Omit<ReactDatePickerProps, 'placeholderText' | 'selected'> & {
  invalid?: boolean;
  placeholder?: string;
};

export const DatePickerFormik: React.FC<DatePickerFormikProps> = ({
  invalid,
  label,
  name,
  ...props
}) => {
  const [field, meta, helper] = useField(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;

  const handleChange = (date: Date | null) => {
    const isBetweenMinMax =
      dayjs(date).hour() >= dayjs(props.minTime).hour() &&
      dayjs(date).hour() <= dayjs(props.maxTime).hour();

    if (dayjs(date).format('DDMMYYYY') === dayjs().format('DDMMYYYY')) {
      // To check current date and time
      const isSameDayBetween =
        Number(dayjs().format('YYYYMMDDHHmm')) >=
          Number(dayjs(props.minTime).format('YYYYMMDDHHmm')) &&
        Number(dayjs().format('YYYYMMDDHHmm')) <=
          Number(dayjs(props.maxTime).format('YYYYMMDDHHmm'));

      if (isSameDayBetween) {
        helper.setValue(date, true);
      } else {
        alert('Please select a valid date and time');
        helper.setValue(undefined, true);
      }
    } else {
      if (isBetweenMinMax) {
        helper.setValue(date, true);
      } else {
        if (dayjs(date).hour() < dayjs(props.minTime).hour()) {
          helper.setValue(dayjs(date).set('h', dayjs(props.minTime).hour()), true);
        }
        if (dayjs(date).hour() > dayjs(props.maxTime).hour()) {
          helper.setValue(dayjs(date).set('h', dayjs(props.maxTime).hour()), true);
        }
      }
    }
  };

  return (
    <Field name={name}>
      {label && (
        <label className="inline-block mb-1 uppercase" htmlFor={`datepicker-${name}`}>
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      <DatePicker
        {...field}
        {...props}
        id={`datepicker-${name}`}
        invalid={invalid ?? showError}
        onChange={handleChange}
      />
    </Field>
  );
};

export type DatePickerFormikProps = MarkOptional<DatePickerProps, 'onChange'> & {
  label?: string;
  name: string;
};

export default DatePicker;
